'use strict';
import { addEvent } from '../addEvent/addEvent';

export const mainPageBaner = class {
  mainContainer = null;
  ulContainer = null;

  contentTxtContainer = null;
  contentBgContainer = null;
  controlContainer = null;

  elements = [];
  curentElement = 0;
  curentTxtElement = null;
  curentImgElement = null;

  inProgress = false;

  autoChange = true;
  autoChangeTime = 8000;
  autoChangeTimer = null;

  letterInAnimationTime = 650;
  letterOutAnimationTime = 650;
  letterDeleyAnimationTime = 100;

  // classes
  classes = {
    list: 'main-page-baner__list',
    contentContainer: 'show-container__element',
    txtWrapper: 'show-container__contentContainer',
    bgContainer: 'main-page-baner__bgContainer',
    firstSlide: 'main-page-baner__image--first-slide',

    in: 'main-page-baner__in-animation',
    out: 'main-page-baner__out-animation',

    controls: 'main-page-baner__slides-control',
    controlsShow: 'main-page-baner__slides-control--show',
    btn: 'slides-control__change-slide',
    prev: 'slides-control__change-slide--prev',
    next: 'slides-control__change-slide--next'
  };

  constructor(mainContainer) {
    if (!mainContainer) return;
    this.mainContainer = mainContainer;
  }

  // -------------------- utils --------------------

  _qs(parent, sel) {
    return parent?.querySelector(sel) || null;
  }

  _text(el) {
    return el?.textContent?.trim() || '';
  }

  _splitWords(text) {
    return text ? text.split(/\s+/) : [];
  }

  _setStyle(el, prop, value) {
    if (el) el.style.setProperty(prop, value);
  }

  _safeRemove(el) {
    if (el && el.remove) el.remove();
  }

  // -------------------- text animation --------------------

  _wrapWords(text, startIndex = 0) {
    const words = this._splitWords(text);

    return words.map((word, i) => {
      const span = document.createElement('span');
      span.textContent = word;

      span.style.setProperty('--animation-duration', `${this.letterInAnimationTime}ms`);
      span.style.setProperty('--animation-delay', `${this.letterDeleyAnimationTime * (i + startIndex)}ms`);
      span.style.setProperty('--animation-out-delay', `${this.letterDeleyAnimationTime * i}ms`);

      return span.outerHTML;
    }).join(' ');
  }

  _prepareTextBlock(container) {
    const title = this._qs(container, '.title');
    if (!title) return { total: 0 };

    const row1 = this._qs(title, '.row__one');
    const row2 = this._qs(title, '.row__two');
    const row3 = this._qs(title, '.row__three');
    const more = this._qs(container, '.main-page-baner__more-info');

    let offset = 0;

    const process = (el) => {
      const text = this._text(el);
      const count = this._splitWords(text).length;

      if (el && text) {
        el.innerHTML = this._wrapWords(text, offset);
      }

      offset += count;
      return count;
    };

    const c1 = process(row1);
    const c2 = process(row2);
    const c3 = process(row3);

    this._setStyle(row3, '--animation-delay-in', `${this.letterDeleyAnimationTime * (c1 + c2)}ms`);

    this._setStyle(more, '--animation-delay', `${this.letterDeleyAnimationTime * (c1 + c2 + c3)}ms`);

    return {
      total: c1 + c2 + c3 + 1
    };
  }

  // -------------------- init --------------------

  init = () => {
    this.ulContainer = this._qs(this.mainContainer, `.${this.classes.list}`);
    if (!this.ulContainer) return;

    const items = Array.from(this.ulContainer.children).filter(el => el.tagName === 'LI');

    items.forEach(li => {
      const txt = this._qs(li, '.main-page-baner__content')?.cloneNode(true);
      const img = this._qs(li, '.main-page-baner__image')?.cloneNode(true);

      if (!txt || !img) return; // skip broken slide

      this._safeRemove(this._qs(txt, '.main-page-baner__image'));

      const { total } = this._prepareTextBlock(txt);

      const time = li.getAttribute('data-time') || this.autoChangeTime;

      this.elements.push({
        txt,
        img,
        time,
        animTime: this.letterDeleyAnimationTime * total + this.letterInAnimationTime
      });
    });

    this._safeRemove(this.ulContainer);

    this._createContainers();
    this._createControls();
  };

  _createContainers() {
    this.contentTxtContainer = document.createElement('div');
    this.contentTxtContainer.classList.add(this.classes.txtWrapper);

    this.contentBgContainer = document.createElement('div');
    this.contentBgContainer.classList.add(this.classes.bgContainer);

    this.mainContainer.append(this.contentTxtContainer, this.contentBgContainer);
  }

  // -------------------- start --------------------

  start = () => {
    if (!this.elements.length) return;

    this._renderSlide(this.curentElement, true);

    if (this.elements.length > 1) {
      setTimeout(() => this._setTimer(), this.elements[0].animTime);
    }
  };

  _renderSlide(index, first = false) {
    const el = this.elements[index];

    const txt = document.createElement('div');
    txt.classList.add(this.classes.contentContainer);
    txt.appendChild(el.txt.cloneNode(true));

    const img = el.img.cloneNode(true);

    if (first) img.classList.add(this.classes.firstSlide);

    this.contentTxtContainer.appendChild(txt);
    this.contentBgContainer.appendChild(img);

    requestAnimationFrame(() => {
      txt.classList.add(this.classes.in);
      img.classList.add(this.classes.in);
    });

    this.curentTxtElement = txt;
    this.curentImgElement = img;
  }

  // -------------------- change --------------------

  _changeSlide = (next) => {
    if (this.inProgress) return;

    const nextEl = this.elements[next];
    if (!nextEl) return;

    this.inProgress = true;

    const newTxt = document.createElement('div');
    newTxt.classList.add(this.classes.contentContainer);
    newTxt.appendChild(nextEl.txt.cloneNode(true));

    const newImg = nextEl.img.cloneNode(true);

    this.contentTxtContainer.appendChild(newTxt);
    this.contentBgContainer.appendChild(newImg);

    this.curentTxtElement.classList.replace(this.classes.in, this.classes.out);

    setTimeout(() => {
      this.curentImgElement.classList.replace(this.classes.in, this.classes.out);
      newTxt.classList.add(this.classes.in);
      newImg.classList.add(this.classes.in);
    }, this.letterOutAnimationTime);

    setTimeout(() => {
      this._safeRemove(this.curentTxtElement);
      this._safeRemove(this.curentImgElement);

      this.curentTxtElement = newTxt;
      this.curentImgElement = newImg;
      this.curentElement = next;
      this.inProgress = false;

      this._setTimer();
    }, this.elements[this.curentElement].animTime);
  };

  _setTimer = () => {
    if (!this.autoChange || this.elements.length < 2) return;

    clearTimeout(this.autoChangeTimer);

    const next = (this.curentElement + 1) % this.elements.length;

    this.autoChangeTimer = setTimeout(() => {
      this._changeSlide(next);
    }, this.elements[this.curentElement].time);
  };

  // -------------------- controls --------------------

  _createControls() {
    if (this.elements.length < 2) return;

    const wrap = document.createElement('div');
    wrap.classList.add(this.classes.controls);

    const makeBtn = (dir, step) => {
      const btn = document.createElement('div');
      btn.classList.add(this.classes.btn, this.classes[dir]);

      addEvent(btn, 'click', () => {
        if (this.inProgress) return;

        clearTimeout(this.autoChangeTimer);

        let next = this.curentElement + step;

        if (next < 0) next = this.elements.length - 1;
        if (next >= this.elements.length) next = 0;

        this._changeSlide(next);
      });

      return btn;
    };

    wrap.append(makeBtn('prev', -1), makeBtn('next', 1));

    this.mainContainer.appendChild(wrap);
    this.controlContainer = wrap;
  }
};
